import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

export default function YaazhBranchesPage() {
  const { enqueueSnackbar } = useSnackbar();

  // State declarations
  const [yaazhBranches, setYaazhBranches] = useState([]);
  const [isYaazhChanged, setIsYaazhChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = user.id || null;

  // Fetch bank branches from API
  const fetchBankBranch = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.get("user/bank-branch");
      if (res?.status === 200) {
        setBanks(res?.data?.bankBranches || []);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      enqueueSnackbar("An error occurred while fetching data.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Load Yaazh branches from localStorage and fetch from API
  useEffect(() => {
    const yaazhBranchesLocal = user.branch_tab || [];
    setYaazhBranches(yaazhBranchesLocal);
    fetchBankBranch();
  }, []);

  // Update localStorage and state
  const updateYaazhBranchesInLocal = (branches) => {
    const updatedUser = { ...user, branch_tab: branches || [] };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setYaazhBranches(branches || []);
  };

  // DataGrid columns
  const yaazhColumns = [
    { field: "branch", headerName: "Branch Name", flex: 1, editable: true },
    { field: "branch_code", headerName: "Branch Code", flex: 1, editable: true },
    { field: "phone_no", headerName: "Phone No.", flex: 1, editable: true },
    { field: "address", headerName: "Address", flex: 2, editable: true },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDeleteYaazhBranch(params.id)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  // Process row update
  const processYaazhUpdate = useCallback(
    (newRow, oldRow) => {
      if (
        newRow.branch !== oldRow.branch ||
        newRow.branch_code !== oldRow.branch_code ||
        newRow.phone_no !== oldRow.phone_no ||
        newRow.address !== oldRow.address
      ) {
        setIsYaazhChanged(true);
      }
      setYaazhBranches((prev) =>
        prev.map((branch) => (branch.id === newRow.id ? newRow : branch))
      );
      return newRow;
    },
    [setYaazhBranches]
  );

  // Add a new branch
  const handleAddYaazhBranch = () => {
    setYaazhBranches((prev) => [
      ...prev,
      { id: "", branch: "", branch_code: "", phone_no: "", address: "" },
    ]);
    setIsYaazhChanged(true);
  };

  // Save Yaazh branches
  const handleSaveYaazhBranches = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.post("user/modify_yaazh_branches", yaazhBranches);
      if (res?.status === 200) {
        updateYaazhBranchesInLocal(res?.data?.yaazhBranches || []);
        enqueueSnackbar("Yaazh branches modified successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to modify yaazh branches!", { variant: "error" });
      }
    } catch (error) {
      console.error("Save error:", error);
      enqueueSnackbar("An error occurred while saving data.", { variant: "error" });
    } finally {
      setLoading(false);
      setIsYaazhChanged(false);
    }
  };

  // Delete a branch
  const handleDeleteYaazhBranch = async (id) => {
    if (!window.confirm("Are you sure you want to delete this branch?")) return;

    try {
      setLoading(true);
      const request = { id, deleted_by: userId };
      const res = await ApiServices.post("user/delete_yaazh_branch", request);

      if (res?.status === 200) {
        updateYaazhBranchesInLocal(res?.data?.yaazhBranches || []);
        enqueueSnackbar("Yaazh branch deleted successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to delete yaazh branch!", { variant: "error" });
      }
    } catch (error) {
      console.error("Delete error:", error);
      enqueueSnackbar("An error occurred while deleting the branch.", { variant: "error" });
    } finally {
      setLoading(false);
      setIsYaazhChanged(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6" gutterBottom>
              Yaazh Branches
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddYaazhBranch}
              >
                Add Branch
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                style={{ marginLeft: 15 }}
                onClick={handleSaveYaazhBranches}
                disabled={!isYaazhChanged}
              >
                Save
              </Button>
            </Box>
          </Grid>

          <div style={{ height: 400, width: "100%", marginTop: 20 }}>
            <DataGrid
              rows={yaazhBranches}
              columns={yaazhColumns}
              editMode="row"
              processRowUpdate={processYaazhUpdate}
              pageSize={5}
              rowsPerPageOptions={[5]}
              disableSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
            />
          </div>
    </Paper>
  );
}
